import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { moneyExact } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { Modal } from "./Projects";
import { Plus, Trash2, Receipt, FileSignature, FileText } from "lucide-react";

const ITEM_TYPES = [
  { value: "material", label: "Material" },
  { value: "labor", label: "Labor" },
  { value: "product", label: "Product" },
  { value: "material_labor", label: "Material & Labor" },
  { value: "service", label: "Service" },
];

const UNIT_OPTIONS = ["each", "sqft", "linear ft", "hour", "day", "cu ft", "gallon", "lump sum"];

function calcUnitPrice(li) {
  const cost = Number(li.unit_cost || 0);
  const markup = Number(li.markup_pct || 0) / 100;
  const proc = Number(li.processing_fee_pct || 0) / 100;
  if (cost > 0) return +(cost * (1 + markup) * (1 + proc)).toFixed(2);
  return +Number(li.unit_price || 0).toFixed(2);
}

function calcLine(li) {
  return +(Number(li.quantity || 0) * calcUnitPrice(li)).toFixed(2);
}

function totals(items, taxRate = 0.0825) {
  const subtotal = items.reduce((acc, i) => acc + calcLine(i), 0);
  const tax = subtotal * taxRate;
  return { subtotal: +subtotal.toFixed(2), tax: +tax.toFixed(2), total: +(subtotal + tax).toFixed(2) };
}

const blankLine = {
  description: "",
  item_type: "material",
  quantity: 1,
  unit: "each",
  unit_cost: 0,
  markup_pct: 0,
  processing_fee_pct: 0,
  unit_price: 0,
  color: "",
  amount: 0,
};

export function LineItemsEditor({ items, setItems, taxRate, setTaxRate }) {
  const t = totals(items, taxRate);

  const addLine = () => setItems([...items, { ...blankLine }]);
  const removeLine = (idx) => setItems(items.filter((_, i) => i !== idx));
  const updateLine = (idx, patch) => {
    const next = items.map((li, i) => i === idx ? { ...li, ...patch } : li);
    // Recompute computed fields for display
    next[idx].unit_price = calcUnitPrice(next[idx]);
    next[idx].amount = calcLine(next[idx]);
    setItems(next);
  };

  return (
    <div className="space-y-3">
      {items.map((li, i) => {
        const up = calcUnitPrice(li);
        const total = calcLine(li);
        return (
          <div key={i} className="rounded-lg p-4 space-y-3" style={{background:'rgba(11,19,43,0.5)', border:'1px solid var(--border-color)'}} data-testid={`line-${i}`}>
            {/* Row 1: Item + Type + Color */}
            <div className="grid grid-cols-12 gap-3 items-start">
              <div className="col-span-12 md:col-span-6">
                <LineLabel>Item</LineLabel>
                <input className="input" value={li.description} onChange={(e)=>updateLine(i,{description: e.target.value})} placeholder="e.g. Custom walnut cabinetry" data-testid={`line-${i}-desc`}/>
              </div>
              <div className="col-span-6 md:col-span-3">
                <LineLabel>Type</LineLabel>
                <select className="input" value={li.item_type || "material"} onChange={(e)=>updateLine(i,{item_type: e.target.value})} data-testid={`line-${i}-type`}>
                  {ITEM_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div className="col-span-5 md:col-span-2">
                <LineLabel>Color</LineLabel>
                <input className="input" value={li.color || ""} onChange={(e)=>updateLine(i,{color: e.target.value})} placeholder="optional" data-testid={`line-${i}-color`}/>
              </div>
              <div className="col-span-1 flex items-end justify-end pt-4">
                <button type="button" onClick={() => removeLine(i)} className="text-red-400 hover:text-red-300 p-2" data-testid={`line-${i}-remove`}><Trash2 size={15}/></button>
              </div>
            </div>

            {/* Row 2: numbers — equal 7-col grid on md+ */}
            <div className="grid grid-cols-2 md:grid-cols-7 gap-2">
              <Cell label="Qty">
                <input type="number" step="0.01" className="input" value={li.quantity} onChange={(e)=>updateLine(i,{quantity: e.target.value})} data-testid={`line-${i}-qty`}/>
              </Cell>
              <Cell label="Unit">
                <select className="input !pr-7" value={li.unit || "each"} onChange={(e)=>updateLine(i,{unit: e.target.value})} data-testid={`line-${i}-unit`}>
                  {UNIT_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </Cell>
              <Cell label="Unit Cost">
                <div className="relative">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm" style={{color:'var(--text-muted)'}}>$</span>
                  <input type="number" step="0.01" className="input !pl-6 !pr-2" value={li.unit_cost} onChange={(e)=>updateLine(i,{unit_cost: e.target.value})} data-testid={`line-${i}-unit-cost`}/>
                </div>
              </Cell>
              <Cell label="Markup">
                <div className="relative">
                  <input type="number" step="0.1" className="input !pr-6" value={li.markup_pct} onChange={(e)=>updateLine(i,{markup_pct: e.target.value})} data-testid={`line-${i}-markup`}/>
                  <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-sm" style={{color:'var(--text-muted)'}}>%</span>
                </div>
              </Cell>
              <Cell label="Fee" hint="%">
                <div className="relative">
                  <input type="number" step="0.1" className="input !pr-6" value={li.processing_fee_pct} onChange={(e)=>updateLine(i,{processing_fee_pct: e.target.value})} data-testid={`line-${i}-fee`}/>
                  <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-sm" style={{color:'var(--text-muted)'}}>%</span>
                </div>
              </Cell>
              <Cell label="Unit Price" hint="auto">
                <div className="input !bg-transparent !border-dashed text-right font-semibold truncate" style={{color:'var(--accent)'}}>
                  {moneyExact(up)}
                </div>
              </Cell>
              <Cell label="Total">
                <div className="input !bg-transparent !border-dashed text-right font-display text-base font-bold truncate" style={{color:'var(--accent)'}}>
                  {moneyExact(total)}
                </div>
              </Cell>
            </div>
          </div>
        );
      })}

      <button type="button" className="btn-ghost !py-2 !px-4 !text-sm" onClick={addLine} data-testid="line-add"><Plus size={14}/> Add line item</button>

      <div className="border-t border-white/5 mt-5 pt-4 text-sm space-y-1.5">
        <div className="flex justify-between"><span style={{color:'var(--text-muted)'}}>Subtotal</span><span>{moneyExact(t.subtotal)}</span></div>
        <div className="flex justify-between items-center">
          <span style={{color:'var(--text-muted)'}}>Tax rate</span>
          <input type="number" step="0.0001" className="input !w-28 !py-1 !text-sm" value={taxRate} onChange={(e)=>setTaxRate(Number(e.target.value))}/>
        </div>
        <div className="flex justify-between"><span style={{color:'var(--text-muted)'}}>Tax</span><span>{moneyExact(t.tax)}</span></div>
        <div className="flex justify-between font-display text-lg font-bold pt-2 border-t border-white/5"><span>Total</span><span style={{color:'var(--accent)'}}>{moneyExact(t.total)}</span></div>
      </div>
    </div>
  );
}

function LineLabel({ children, hint }) {
  return (
    <div className="text-[10px] uppercase tracking-[0.18em] mb-1 font-semibold flex items-center gap-1.5" style={{color:'var(--text-muted)'}}>
      {children}
      {hint && <span className="text-[9px] normal-case" style={{color:'var(--text-dim)'}}>· {hint}</span>}
    </div>
  );
}

function Cell({ label, hint, children }) {
  return (
    <div>
      <LineLabel hint={hint}>{label}</LineLabel>
      {children}
    </div>
  );
}

export { blankLine };

export default function AdminEstimates() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [editing, setEditing] = useState(null);
  const [converting, setConverting] = useState(null);
  const [saving, setSaving] = useState(null); // "draft" | "sent" | null
  const navigate = useNavigate();

  const load = () => api.get("/estimates").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/clients").then((r) => setClients(r.data));
  }, []);

  const convertToInvoice = async (e) => {
    setConverting(e.id);
    try {
      const { data } = await api.post(`/estimates/${e.id}/convert-to-invoice`);
      load();
      const ok = window.confirm(`✓ Invoice created for ${moneyExact(data.total)}.\n\nGo to Invoices to set the due date and send the payment link?`);
      if (ok) navigate("/admin/invoices");
    } catch (err) {
      alert("Could not convert: " + (err.response?.data?.detail || err.message));
    } finally {
      setConverting(null);
    }
  };

  const openPdf = async (eid) => {
    try {
      const res = await api.get(`/estimates/${eid}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener,noreferrer");
      // Revoke after a bit so the new tab has time to load
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      alert("Could not open PDF: " + (err.response?.data?.detail || err.message));
    }
  };

  const save = async (status = "sent") => {
    if (!editing.client_id) return alert("Select a client");
    if (!editing.title?.trim()) return alert("Add a title");
    if (editing.line_items.length === 0) return alert("Add at least one line item");
    setSaving(status);
    try {
      const { data } = await api.post("/estimates", {
        client_id: editing.client_id,
        project_id: null,
        title: editing.title,
        line_items: editing.line_items,
        notes: editing.notes,
        tax_rate: editing.tax_rate,
        status,
      });
      setEditing(null);
      load();
      if (status === "draft") {
        // Open PDF preview immediately after saving draft
        openPdf(data.id);
      }
    } catch (err) {
      alert("Could not save: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this estimate?")) return;
    await api.delete(`/estimates/${id}`);
    load();
  };

  return (
    <DashboardLayout
      role="admin" title="Estimates"
      subtitle="Build line-item proposals and send them to clients for approval."
      actions={<button className="btn-primary" onClick={() => setEditing({
        client_id: "", title: "", line_items: [{ ...blankLine }], notes: "", tax_rate: 0.0825,
      })} data-testid="new-estimate-btn"><Plus size={16}/> New Estimate</button>}
    >
      {items.length === 0 ? (
        <div className="card p-12 text-center text-sm" style={{color:'var(--text-muted)'}}>
          <FileSignature className="mx-auto mb-3"/>
          No estimates yet.
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full">
            <thead className="text-xs uppercase tracking-wider" style={{color:'var(--text-muted)'}}>
              <tr className="border-b border-white/5">
                <th className="text-left p-4">Title</th>
                <th className="text-left p-4">Client</th>
                <th className="text-left p-4">Status</th>
                <th className="text-right p-4">Total</th>
                <th className="text-right p-4">Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {items.map((e) => {
                const client = clients.find((c) => c.id === e.client_id);
                return (
                  <tr key={e.id} className="border-b border-white/5">
                    <td className="p-4 font-medium">{e.title}</td>
                    <td className="p-4">{client?.name || <span style={{color:'var(--text-muted)'}}>—</span>}</td>
                    <td className="p-4">
                      {e.invoice_id
                        ? <span className="badge badge-green">converted</span>
                        : <span className={`badge ${e.status === 'approved' ? 'badge-green' : (e.status === 'draft' ? '' : '')}`}>{e.status}</span>}
                    </td>
                    <td className="p-4 text-right font-semibold">{moneyExact(e.total)}</td>
                    <td className="p-4 text-right text-xs" style={{color:'var(--text-muted)'}}>{new Date(e.created_at).toLocaleDateString()}</td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openPdf(e.id)}
                          className="btn-ghost !py-1.5 !px-3 !text-xs"
                          data-testid={`pdf-est-${e.id}`}
                          title="Preview PDF"
                        >
                          <FileText size={12}/> PDF
                        </button>
                        {e.status === "approved" && !e.invoice_id && (
                          <button
                            onClick={() => convertToInvoice(e)}
                            disabled={converting === e.id}
                            className="btn-primary !py-1.5 !px-3 !text-xs"
                            data-testid={`convert-est-${e.id}`}
                          >
                            <Receipt size={12}/> {converting === e.id ? "Converting…" : "Convert to Invoice"}
                          </button>
                        )}
                        <button className="text-red-400 hover:text-red-300" onClick={() => remove(e.id)} data-testid={`delete-est-${e.id}`}><Trash2 size={14}/></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <Modal onClose={() => setEditing(null)} title="New Estimate" size="6xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
            <label className="block">
              <div className="text-xs uppercase tracking-wider mb-2 font-semibold" style={{color:'var(--text-muted)'}}>Client</div>
              <select className="input" value={editing.client_id} onChange={(e) => setEditing({...editing, client_id: e.target.value})} data-testid="est-client">
                <option value="">Select client…</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.email})</option>)}
              </select>
            </label>
            <label className="block">
              <div className="text-xs uppercase tracking-wider mb-2 font-semibold" style={{color:'var(--text-muted)'}}>Title</div>
              <input className="input" value={editing.title} onChange={(e) => setEditing({...editing, title: e.target.value})} data-testid="est-title"/>
            </label>
          </div>
          <LineItemsEditor
            items={editing.line_items}
            setItems={(li) => setEditing({...editing, line_items: li})}
            taxRate={editing.tax_rate}
            setTaxRate={(r) => setEditing({...editing, tax_rate: r})}
          />
          <label className="block mt-5">
            <div className="text-xs uppercase tracking-wider mb-2 font-semibold" style={{color:'var(--text-muted)'}}>Notes</div>
            <textarea className="input" rows={3} value={editing.notes} onChange={(e) => setEditing({...editing, notes: e.target.value})}/>
          </label>
          <div className="flex gap-3 justify-end mt-6">
            <button className="btn-ghost" onClick={() => setEditing(null)} disabled={!!saving} data-testid="est-cancel">Cancel</button>
            <button
              className="btn-ghost"
              onClick={() => save("draft")}
              disabled={!!saving}
              data-testid="est-save-draft"
              title="Save as draft and preview the PDF"
            >
              <FileText size={14}/> {saving === "draft" ? "Saving…" : "Save as Draft & Preview PDF"}
            </button>
            <button
              className="btn-primary"
              onClick={() => save("sent")}
              disabled={!!saving}
              data-testid="est-save"
            >
              {saving === "sent" ? "Saving…" : "Save & Send"}
            </button>
          </div>
        </Modal>
      )}
    </DashboardLayout>
  );
}
